import { GetSurveyRuleTargetOptionsRPC } from "@pulse/pulse-rpcs/lib/survey-rules/getSurveyRuleTargetOptionsRPC";
import { SurveyRuleAction } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleAction";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  AutoCompleteItem,
  MultiAutoCompleteInputField,
} from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import { Instance, SnapshotOut } from "mobx-state-tree";
import React, { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import {
  processSurveyParams,
  useRouteNavigator,
} from "../../../../routes/RoutesHelper";
import { SurveyRuleModel } from "../../models/SurveyRuleModel";
import { SurveyRuleTargetModel } from "../../models/SurveyRuleTargetModel";
import { SurveyRuleQuestionKeyTypeModel } from "../../models/SurveyRuleQuestionKeyTypeModel";
import {
  useGetSurveyRuleTargetOptionsStore,
  useSurveyRuleStore,
} from "../../stores/Hooks";
import { getOptionsWithSelectedAutoCompleteValues } from "../../utils/getOptionsWithSelectedAutoCompleteValues";
import { getTargetValidationErrorHelperText } from "../../utils/getSurveyRuleValidationErrorHelperText";
import { SurveyRuleConditionValidationError } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleConditionValidationError";

interface SurveyRuleTargetInputFieldProps {
  surveyAction: SurveyRuleAction.SurveyRuleAction | null;
  surveyRuleId: string;
  surveyTarget: Instance<typeof SurveyRuleTargetModel>[];
}

const TARGET_KEY_FIELD_WIDTH = "280px";
const TARGET_VALUE_SEPARATOR = "_";

const getTargetValue = (
  target: Instance<typeof SurveyRuleTargetModel>,
): string =>
  target.gridRowCode
    ? `${target.questionCode}${TARGET_VALUE_SEPARATOR}${target.gridRowCode}`
    : target.questionCode;

const mapTargetToAutoCompleteItem = (
  target: Instance<typeof SurveyRuleTargetModel>,
): AutoCompleteItem => {
  const value = getTargetValue(target);
  return {
    id: value,
    label: value,
  };
};

const mapTargetValueToSurveyRuleTarget = (
  value: string,
): SnapshotOut<typeof SurveyRuleTargetModel> => {
  const [questionCode, ...gridRowCodeParts] = value.split(
    TARGET_VALUE_SEPARATOR,
  );
  return {
    questionCode: questionCode ?? EMPTY_CHARACTER,
    gridRowCode: gridRowCodeParts.length
      ? gridRowCodeParts.join(TARGET_VALUE_SEPARATOR)
      : null,
  };
};

const getTargetQuestionKeyType = (
  surveyRule: Instance<typeof SurveyRuleModel>,
): Instance<typeof SurveyRuleQuestionKeyTypeModel> | null => {
  const targetConditionId = Array.from(surveyRule.conditionOrder)
    .reverse()
    .find((conditionId) => {
      const condition = surveyRule.getNonNullCondition(conditionId);
      return condition.connecter === "NONE";
    });
  if (!targetConditionId) return null;

  const condition = surveyRule.getNonNullCondition(targetConditionId);
  return condition.keyType.type === "QUESTION" ? condition.keyType : null;
};

export const SurveyRuleTargetInputField: React.FC<SurveyRuleTargetInputFieldProps> =
  observer(({ surveyAction, surveyRuleId, surveyTarget }) => {
    const [openUnexpectedErrorDialog, setOpenUnexpectedErrorDialog] =
      useState(false);

    const { t } = useTranslation();
    const navigateTo = useRouteNavigator();
    const targetKeyFieldId = useId();

    const surveyRuleStore = useSurveyRuleStore();
    const getSurveyRuleTargetOptionsStore =
      useGetSurveyRuleTargetOptionsStore();

    const surveyRule = surveyRuleStore.getNonNullRule(surveyRuleId);
    const targetQuestionKeyType = getTargetQuestionKeyType(surveyRule);
    const targetQuestionCode = targetQuestionKeyType?.conditionKey ?? null;
    const targetOptionsRequestKey = [
      surveyRuleId,
      surveyAction ?? EMPTY_CHARACTER,
      targetQuestionCode ?? EMPTY_CHARACTER,
    ].join(":");
    const targetOptions = getSurveyRuleTargetOptionsStore.targetOptions.get(
      targetOptionsRequestKey,
    );
    const selectedTargetValues = surveyTarget.map(mapTargetToAutoCompleteItem);
    const targetKeyOptions =
      targetOptions?.map(mapTargetToAutoCompleteItem) ?? [];
    const targetKeyOptionsWithSelectedValues =
      getOptionsWithSelectedAutoCompleteValues(
        targetKeyOptions,
        selectedTargetValues,
      );
    const targetSnapshotsByValue = new Map<
      string,
      SnapshotOut<typeof SurveyRuleTargetModel>
    >(
      (targetOptions ?? []).map((target) => [
        getTargetValue(target),
        {
          questionCode: target.questionCode,
          gridRowCode: target.gridRowCode,
        },
      ]),
    );
    const isTargetKeyLoading =
      getSurveyRuleTargetOptionsStore.loadingGetSurveyRuleTargetOptions.get(
        targetOptionsRequestKey,
      ) ?? false;
    const targetHelperText = getTargetValidationErrorHelperText(
      t,
      surveyRule.targetValidationErrors,
    );

    useEffect(() => {
      const targetKeyFieldDOMElement =
        document.getElementById(targetKeyFieldId);
      if (!targetKeyFieldDOMElement) return;

      const handleTargetKeyFieldFocus = async (): Promise<void> => {
        if (!surveyAction) return;

        await processSurveyParams(async (surveyId) => {
          const rpcError = await getSurveyRuleTargetOptionsStore
            .getSurveyRuleTargetOptions(
              surveyId,
              surveyAction,
              targetQuestionCode,
              targetOptionsRequestKey,
            )
            .catch(() => {
              setOpenUnexpectedErrorDialog(true);
              return null;
            });

          switch (rpcError) {
            case null:
              break;
            case undefined:
              targetQuestionKeyType?.clearConditionKeyValidationErrors();
              break;
            case GetSurveyRuleTargetOptionsRPC.RPCError.InvalidSurveyId:
              navigateTo.pageNotFound();
              break;
            case GetSurveyRuleTargetOptionsRPC.RPCError.InvalidQuestionCode:
              targetQuestionKeyType?.setConditionKeyValidationError(
                SurveyRuleConditionValidationError
                  .SurveyRuleConditionValidationError.INVALID_QUESTION_KEY,
              );
              break;
            case GetSurveyRuleTargetOptionsRPC.RPCError.UnsupportedQuestionType:
              targetQuestionKeyType?.setConditionKeyValidationError(
                SurveyRuleConditionValidationError
                  .SurveyRuleConditionValidationError.INVALID_QUESTION_TYPE,
              );
              break;
            default:
              rpcError satisfies never;
          }
        }, navigateTo);
      };

      targetKeyFieldDOMElement.addEventListener(
        "focus",
        handleTargetKeyFieldFocus,
      );

      return () =>
        targetKeyFieldDOMElement.removeEventListener(
          "focus",
          handleTargetKeyFieldFocus,
        );
    }, [
      getSurveyRuleTargetOptionsStore,
      navigateTo,
      surveyAction,
      targetKeyFieldId,
      targetOptionsRequestKey,
      targetQuestionCode,
    ]);

    const handleTargetChange = (selectedItems: AutoCompleteItem[]): void => {
      surveyRuleStore.setTarget(
        surveyRuleId,
        selectedItems
          .map((selectedItem) =>
            selectedItem.id === null ? null : String(selectedItem.id),
          )
          .filter((value): value is string => value !== null)
          .map(
            (value) =>
              targetSnapshotsByValue.get(value) ??
              mapTargetValueToSurveyRuleTarget(value),
          ),
      );
    };

    return (
      <>
        {openUnexpectedErrorDialog && (
          <UnexpectedErrorDialog
            message={t("common.uploadDialog.unexpectedErrorDescription")}
            onClickDone={() => setOpenUnexpectedErrorDialog(false)}
          />
        )}
        <MultiAutoCompleteInputField
          id={targetKeyFieldId}
          label={EMPTY_CHARACTER}
          placeholder={t("surveys.rules.targetKey")}
          options={targetKeyOptionsWithSelectedValues}
          value={selectedTargetValues}
          onChange={handleTargetChange}
          selectType="checkbox"
          isLoading={isTargetKeyLoading}
          loadingText={t("surveys.rules.loadingTargetOptionsText")}
          noOptionsText={t("surveys.autoCompleteField.noResultsFound")}
          error={surveyRule.targetValidationErrors.length > 0}
          helperText={targetHelperText}
          isDisabled={!surveyAction}
          width={TARGET_KEY_FIELD_WIDTH}
          minWidth={TARGET_KEY_FIELD_WIDTH}
        />
      </>
    );
  });
